import { createSearchParams } from "react-router";

export function parseEnumParam<T>(
  value: string,
  validValues: ReadonlyArray<T>,
): T | undefined {
  const uppercasedValue = value.toUpperCase() as T;
  if (validValues.includes(uppercasedValue)) {
    return uppercasedValue;
  }
  return undefined;
}

export function getParamFromRequest(request: Request, paramName: string) {
  const searchParams = createSearchParams(new URL(request.url).searchParams);
  return searchParams.get(paramName);
}

type RecordType = Readonly<Record<string, PropertyKey>>;
type EnumRegistry = Readonly<Record<Uppercase<string>, RecordType>>;

type Key<R extends EnumRegistry> = Lowercase<Extract<keyof R, string>>;
type Keys<R extends EnumRegistry> = ReadonlyArray<Key<R>>;
type Values<R extends EnumRegistry> = Readonly<
  Record<Key<R>, Readonly<R[Extract<keyof R, Uppercase<Key<R>>>]>>
>;

type VariantUnion<R extends EnumRegistry> = R[keyof R];
type Value<R extends EnumRegistry> = Readonly<{
  [K in keyof VariantUnion<R>]: VariantUnion<R>[K];
}>;

type KeyOf<R extends EnumRegistry> = (value: Value<R>) => Key<R> | undefined;
interface DefineEnum<R extends EnumRegistry> {
  keys: Keys<R>;
  values: Values<R>;
  keyOf: KeyOf<R>;
}

const canonicalize = <R extends EnumRegistry>(value: Value<R>): string => {
  const sortedEntries = Object.entries(value)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, val]) => {
      if (typeof val === "object" && val !== null) {
        return [key, canonicalize({ ...val } as Value<R>)];
      }
      return [key, String(val)];
    });
  return JSON.stringify(Object.fromEntries(sortedEntries));
};

const buildLookup = <R extends EnumRegistry>(
  values: Values<R>,
): Map<string, Key<R>> => {
  const lookup = new Map<string, Key<R>>();
  for (const [key, value] of Object.entries(values) as Array<
    [Key<R>, RecordType]
  >) {
    lookup.set(canonicalize(value), key);
  }
  return lookup;
};

export const defineEnum = <const R extends EnumRegistry>(
  registry: R,
): DefineEnum<R> => {
  const keys = Object.freeze(
    Object.keys(registry).map((key) => key.toLowerCase()),
  ) as Keys<R>;

  const values = Object.freeze(
    Object.fromEntries(
      Object.entries(registry).map(([key, value]) => [
        key.toLowerCase(),
        Object.freeze(value),
      ]),
    ),
  ) as Values<R>;

  const lookup = buildLookup<R>(values);
  const keyOf: KeyOf<R> = (value) => lookup.get(canonicalize(value));

  return Object.freeze({ keys, values, keyOf }) as DefineEnum<R>;
};
